import { IRoutineFrequency, IRoutineRepeatType } from "@propely/propely-typings";
import moment from "moment";

export const decodeFrequency = (value: string, startDate?: string, defaultEach?: number): IRoutineFrequency | undefined => {
  if (!value) return undefined;
  let string = value.toLowerCase();
  // Removes unnecessary words
  const valuesToReplace = [".", ","];
  valuesToReplace.forEach((v) => {
    string = string.split(v).join("");
  });

  // Converts to a word array and removes empty strings
  const wordsToIgnore = [" ", "the", "den"];
  const words = string.split(" ").filter((w) => !wordsToIgnore.includes(w));

  // Return value
  const frequency: IRoutineFrequency = {
    each: defaultEach || 1,
  };

  // Tries to find an "each"
  const everyOther_otherTerms = ["andre", "other"];
  for (const term of ["hvert", "hver", "every", "each"]) {
    const index = words.indexOf(term);
    if (index !== -1) {
      const each = words[index + 1];
      if (each && !isNaN(+each)) {
        frequency.each = +each;
        words.splice(index, 2);
      } else if (everyOther_otherTerms.includes(each)) {
        frequency.each = 2;
        words.splice(index, 2);
      } else {
        words.splice(index, 1);
      }
    }
  }

  // Finds "every other"
  if (["annenhver", "every other"].some((term) => words.includes(term))) {
    frequency.each = 2;
  }

  const dateMoment = startDate ? parseDateStringToMoment(startDate) : undefined;

  // Static terms to check for
  const monthTerms: { [key: number]: string[] } = {
    0: ["january", "januar", "jan"],
    1: ["february", "februar", "feb"],
    2: ["march", "mars", "mar"],
    3: ["april", "april", "apr"],
    4: ["may", "mai"],
    5: ["june", "juni", "jun"],
    6: ["july", "juli", "jul"],
    7: ["august", "august", "aug"],
    8: ["september", "september", "sep"],
    9: ["october", "oktober", "oct", "okt"],
    10: ["november", "november", "nov"],
    11: ["december", "desember", "dec", "des"],
  };
  const repeatTypeTerms: { [key: string]: string[] } = {
    "1": ["first", "første"],
    "2": ["second", "andre"],
    "3": ["third", "tredje"],
    "4": ["fourth", "fjerde"],
    last: ["last", "siste"],
  };
  const weekdayTerms: { [key: number]: string[] } = {
    0: ["sunday", "søndag", "sun", "søn"],
    1: ["monday", "mandag", "mon", "man"],
    2: ["tuesday", "tirsdag", "tue", "tir"],
    3: ["wednesday", "onsdag", "wed", "ons"],
    4: ["thursday", "torsdag", "thu", "tor"],
    5: ["friday", "fredag", "fri", "fre"],
    6: ["saturday", "lørdag", "sat", "lør"],
  };

  // Calculates the frequency based on a start date
  if (startDate && dateMoment) {
    if (["årlig", "år", "yearly", "year"].some((term) => string.includes(term))) {
      frequency.yearly = {
        months: [dateMoment.month()],
        repeatDate: dateMoment.date(),
      };
    } else if (["månedlig", "måned", "monthly", "month"].some((term) => string.includes(term))) {
      frequency.monthly = {
        repeatDate: dateMoment.date(),
      };
    } else if (["ukentlig", "uke", "weekly", "week", "weeks"].some((term) => string.includes(term))) {
      frequency.weekly = {
        days: [dateMoment.weekday()],
      };
    }
  } else {
    // Calculating repeating type (first, second, etc)
    let repeatType: string | undefined = undefined;
    for (const key of Object.keys(repeatTypeTerms)) {
      const repeatTerm = words.find((w) => repeatTypeTerms[key].includes(w));
      if (repeatTerm) {
        repeatType = key;
        words.splice(words.indexOf(repeatTerm), 1);
      }
    }

    // Calculating the weekday
    const repeatWeekdayString = Object.keys(weekdayTerms).find((key) => weekdayTerms[+key].some((term) => words.includes(term)));
    const repeatWeekday = repeatWeekdayString && !isNaN(+repeatWeekdayString) ? +repeatWeekdayString : undefined;

    // If there is no weekday, it checks for a day by looking for any remaining numbers
    let repeatDate: number | undefined = undefined;
    if (!repeatWeekday && !repeatType) {
      const date = words.find((w) => !isNaN(+w));
      if (date) {
        repeatDate = +date;
        words.splice(words.indexOf(date), 1);
      }
    }

    const months: number[] = [];
    for (const key of Object.keys(monthTerms)) {
      const month = monthTerms[+key].find((term) => words.includes(term));
      if (month) {
        months.push(+key);
      }
    }

    if (["årlig", "år", "yearly", "year"].some((term) => words.includes(term))) {
      frequency.yearly = {
        months: months,
        repeatWeekday: repeatWeekday,
        repeatDate: repeatDate,
        repeatType: repeatType as IRoutineRepeatType,
      };
    } else if (["månedlig", "måned", "mnd", "monthly", "month", "mth"].some((term) => words.includes(term))) {
      frequency.monthly = {
        repeatWeekday: repeatWeekday,
        repeatDate: repeatDate,
        repeatType: repeatType as IRoutineRepeatType,
      };
    } else if (["ukentlig", "uke", "weekly", "week"].some((term) => words.includes(term))) {
      let days: number[] = [];
      for (const weekdayTerm of Object.keys(weekdayTerms)) {
        if (weekdayTerms[+weekdayTerm].some((term) => string.includes(term))) {
          days.push(+weekdayTerm);
        }
      }
      frequency.weekly = {
        days: days,
      };
    } else {
      // Check for months and set it to yearly
      if (months.length > 0) {
        frequency.yearly = {
          months: months,
          repeatWeekday: repeatWeekday,
          repeatDate: repeatDate,
          repeatType: repeatType as IRoutineRepeatType,
        };
      }
      // Checks for just weekdays, if no other frequency is set, we set it to weeklyv
      else if (checkIfRoutineFrequencyIsEmpty(frequency) && Object.values(weekdayTerms).some((terms) => words.some((word) => terms.includes(word)))) {
        const days = Object.keys(weekdayTerms).filter((key) => weekdayTerms[+key].some((term) => words.includes(term)));
        frequency.weekly = {
          days: days.map((d) => +d),
        };
      } else {
        // Checks for running or daily
        if (checkIfRoutineFrequencyIsEmpty(frequency) && ["daglig", "dag", "daily", "day"].some((daily) => words.includes(daily))) {
          frequency.daily = {};
        }
      }
    }
  }

  // If we have not set a frequency field except for each, we set it to running
  if (checkIfRoutineFrequencyIsEmpty(frequency)) {
    frequency.running = {};
  }

  return removeEmptyProperties(frequency);
};

const checkIfRoutineFrequencyIsEmpty = (frequency: IRoutineFrequency) => {
  return !frequency.daily && !frequency.weekly && !frequency.monthly && !frequency.yearly && !frequency.running;
};

const parseDateStringToMoment = (dateString: string) => {
  const dateFormats = [
    "DD.MM.YYYY",
    "DD/MM/YYYY",
    "DD-MM-YYYY", // NO
    "MM.DD.YYYY",
    "MM/DD/YYYY",
    "MM-DD-YYYY", // US
    "YYYY.MM.DD",
    "YYYY/MM/DD",
    "YYYY-MM-DD", // ISO
  ];

  for (const format of dateFormats) {
    const date = moment(dateString, format);
    if (date.isValid()) {
      return date;
    }
  }
  return undefined;
};

// Removes undefined values from an object and all of its sub-values
const removeEmptyProperties = (obj: any) => {
  Object.keys(obj).forEach((key) => {
    if (obj[key] && typeof obj[key] === "object") {
      removeEmptyProperties(obj[key]);
    } else if (obj[key] == null) {
      delete obj[key];
    }
  });
  return obj;
};
